export type MatchingDraftRow = { left: string; right: string };

export const matchingValueText = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => matchingValueText(item).trim()).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const preferredKeys = [
      'text',
      'label',
      'value',
      'name',
      'title',
      'term',
      'definition',
      'word',
      'answer',
      'match',
      'target',
      'prompt',
      'question',
      'content',
    ];
    for (const key of preferredKeys) {
      const nextValue = value[key];
      const text = matchingValueText(nextValue).trim();
      if (text) return text;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
};

const matchingRowFromValue = (row: any, index?: number): MatchingDraftRow => {
  if (Array.isArray(row)) {
    return {
      left: matchingValueText(row[0]),
      right: matchingValueText(row[1]),
    };
  }
  if (row && typeof row === 'object') {
    const leftValue = row.left ?? row.term ?? row.prompt ?? row.question ?? row.source ?? row.item ?? row.key;
    const rightValue = row.right ?? row.match ?? row.answer ?? row.definition ?? row.target ?? row.value ?? row.text ?? row.label;
    if (leftValue !== undefined || rightValue !== undefined) {
      return {
        left: matchingValueText(leftValue),
        right: matchingValueText(rightValue),
      };
    }
    const entries = Object.entries(row);
    if (entries.length === 1) {
      const [left, right] = entries[0];
      return {
        left: matchingValueText(left),
        right: matchingValueText(right),
      };
    }
  }
  return {
    left: index === undefined ? '' : String(index),
    right: matchingValueText(row),
  };
};

export const normalizeMatchingDraftRows = (pairs: any): MatchingDraftRow[] => {
  if (!pairs || typeof pairs !== 'object') return [];
  const rows = Array.isArray(pairs)
    ? pairs.map((row, index) => matchingRowFromValue(row, index))
    : Object.entries(pairs).map(([left, right]) => {
        if (right && typeof right === 'object' && !Array.isArray(right)) {
          const row = matchingRowFromValue(right);
          const leftIsArrayIndex = /^\d+$/.test(left);
          if (leftIsArrayIndex && (row.left || row.right)) return row;
          if (row.right) {
            return {
              left: matchingValueText(left),
              right: row.right,
            };
          }
        }
        return {
          left: matchingValueText(left),
          right: matchingValueText(right),
        };
      });
  return rows.filter((row) => row.left.trim() || row.right.trim());
};

export const matchingRowsToPairs = (rows: MatchingDraftRow[]): Record<string, string> => Object.fromEntries(
  rows
    .map((row) => [String(row.left || '').trim(), String(row.right || '').trim()])
    .filter(([left, right]) => left && right)
);

export const matchingPairsToTextLines = (pairs: any): string[] => (
  normalizeMatchingDraftRows(pairs)
    .map((row) => [String(row.left || '').trim(), String(row.right || '').trim()])
    .filter(([left, right]) => left && right)
    .map(([left, right]) => `${left} -> ${right}`)
);
